/**
 * OnboardingProgress — 4-dot progress indicator for the onboarding wizard
 * (spec FR-011, 016 Onboarding Wizard).
 *
 * Three dot states:
 *   - active   (current step)  — wider pill, `bg-primary`
 *   - visited  (past step)     — standard dot, `bg-primary/50` (reduced opacity)
 *   - unvisited (future step)  — standard dot, `bg-muted`
 *
 * Renders as a `<nav>` with a Finnish `aria-label` resolved via
 * `useTranslate()` and `aria-current="step"` on the active dot so assistive
 * tech announces position-in-flow.
 *
 * The component is presentation-only — caller passes `currentStep` and
 * `totalSteps`; no internal state.
 */

import type { JSX } from 'react';

import { useTranslate } from '../i18n/useTranslate.js';

export interface OnboardingProgressProps {
  readonly currentStep: number;
  readonly totalSteps: number;
}

const navClasses = 'flex items-center justify-center gap-2';
const dotBaseClasses = 'h-1 rounded-full transition-all duration-300 ease-out';
const dotActiveClasses = 'w-12 bg-primary';
const dotVisitedClasses = 'w-8 bg-primary/60';
const dotUnvisitedClasses = 'w-8 bg-muted';

export function OnboardingProgress({
  currentStep,
  totalSteps,
}: OnboardingProgressProps): JSX.Element {
  const t = useTranslate();

  // Build the dot list deterministically — index 0..totalSteps-1.
  const dots = Array.from({ length: totalSteps }, (_, index) => {
    let stateClasses = dotUnvisitedClasses;
    if (index === currentStep) {
      stateClasses = dotActiveClasses;
    } else if (index < currentStep) {
      stateClasses = dotVisitedClasses;
    }
    return (
      <span
        // biome-ignore lint/suspicious/noArrayIndexKey: dots have a fixed positional identity (step 0..3); the index IS the stable key
        key={index}
        className={`${dotBaseClasses} ${stateClasses}`}
        aria-current={index === currentStep ? 'step' : undefined}
      />
    );
  });

  return (
    <nav aria-label={t('onboarding.progress.ariaLabel')} className={navClasses}>
      {dots}
    </nav>
  );
}
